import { useState, useEffect, useRef } from 'react';
import Layout from '@/components/feature/Layout';
import { supabase, Producto, Auditoria } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/base/ConfirmDialog';

const emptyForm = {
  nombre: '',
  tipo_producto: '',
  marca: '',
  modelo_compatible: '',
  codigo_interno: '',
  precio_compra: '',
  precio_venta: '',
  stock: '',
  stock_minimo: '',
  proveedor: '',
  ubicacion: '',
};

export default function ProductosPage() {
  const { user } = useAuth();
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [costoAjuste, setCostoAjuste] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [historyPeriod, setHistoryPeriod] = useState<'dia' | 'semana' | 'mes'>('semana');
  const [historyData, setHistoryData] = useState<Auditoria[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const itemsPerPage = 8;

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [prodRes] = await Promise.all([
        supabase.from('productos').select('*').order('created_at', { ascending: false }),
      ]);
      if (prodRes.data) setProductos(prodRes.data);
    } catch {
      showToast('Error al cargar productos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const now = new Date();
      let since = new Date();
      if (historyPeriod === 'dia') {
        since.setHours(0, 0, 0, 0);
      } else if (historyPeriod === 'semana') {
        since.setDate(now.getDate() - 7);
      } else {
        since.setDate(now.getDate() - 30);
      }
      const { data } = await supabase
        .from('auditoria')
        .select('*')
        .eq('modulo', 'productos')
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: false });
      if (data) setHistoryData(data as Auditoria[]);
    } catch {
      // silent
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [historyPeriod]);

  const filtered = productos.filter((p) => {
    const matchSearch =
      search === '' ||
      p.nombre.toLowerCase().includes(search.toLowerCase()) ||
      (p.marca && p.marca.toLowerCase().includes(search.toLowerCase())) ||
      (p.tipo_producto && p.tipo_producto.toLowerCase().includes(search.toLowerCase())) ||
      (p.codigo_interno && p.codigo_interno.toLowerCase().includes(search.toLowerCase()));
    const matchTipo = tipoFilter === '' || p.tipo_producto === tipoFilter;
    return matchSearch && matchTipo;
  });

  const tiposProducto = Array.from(
    new Set(productos.map((p) => p.tipo_producto).filter((t): t is string => !!t))
  ).sort();

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const openNewModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setCostoAjuste('');
    setImageFile(null);
    setImagePreview(null);
    setModalOpen(true);
  };

  const openEditModal = (prod: Producto) => {
    setEditingId(prod.id);
    setForm({
      nombre: prod.nombre,
      tipo_producto: prod.tipo_producto || '',
      marca: prod.marca || '',
      modelo_compatible: prod.modelo_compatible || '',
      codigo_interno: prod.codigo_interno || '',
      precio_compra: String(prod.precio_compra),
      precio_venta: String(prod.precio_venta),
      stock: String(prod.stock),
      stock_minimo: String(prod.stock_minimo),
      proveedor: prod.proveedor || '',
      ubicacion: prod.ubicacion || '',
    });
    setCostoAjuste(String(prod.precio_compra));
    setImageFile(null);
    setImagePreview(prod.imagen_url || null);
    setModalOpen(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Solo se permiten archivos de imagen', 'error');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast('La imagen no puede superar los 5MB', 'error');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return editingId ? null : null;

    const fileExt = imageFile.name.split('.').pop() || 'jpg';
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from('productos')
      .upload(fileName, imageFile, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      showToast(`Error al subir imagen: ${error.message}`, 'error');
      return null;
    }

    const { data: urlData } = supabase.storage.from('productos').getPublicUrl(data.path);
    return urlData.publicUrl;
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      showToast('El nombre del producto es obligatorio', 'error');
      return;
    }

    if (!form.precio_venta || Number(form.precio_venta) <= 0) {
      showToast('El precio de venta debe ser mayor a 0', 'error');
      return;
    }

    setSaving(true);

    try {
      let imagenUrl: string | null = null;

      if (imageFile) {
        imagenUrl = await uploadImage();
        if (!imagenUrl && imageFile) {
          setSaving(false);
          return;
        }
      }

      const productoData = {
        nombre: form.nombre.trim(),
        tipo_producto: form.tipo_producto.trim() || null,
        marca: form.marca.trim() || null,
        modelo_compatible: form.modelo_compatible.trim() || null,
        codigo_interno: form.codigo_interno.trim() || null,
        precio_compra: Number(form.precio_compra) || 0,
        precio_venta: Number(form.precio_venta),
        stock_minimo: Number(form.stock_minimo) || 0,
        proveedor: form.proveedor.trim() || null,
        ubicacion: form.ubicacion.trim() || null,
        ...(imagenUrl ? { imagen_url: imagenUrl } : {}),
      };

      if (editingId) {
        const oldProduct = productos.find((p) => p.id === editingId);
        const oldStock = oldProduct?.stock || 0;
        const newStock = Number(form.stock) || 0;

        // Se actualizan los datos sin tocar el stock; el movimiento de stock se hace via RPC de CPP
        const { error } = await supabase
          .from('productos')
          .update({ ...productoData, stock: oldStock, updated_at: new Date().toISOString() })
          .eq('id', editingId);

        if (error) throw error;

        if (oldStock !== newStock) {
          const diff = newStock - oldStock;
          const { error: movError } = await supabase.rpc('cpp_registrar_movimiento', {
            p_producto_id: editingId,
            p_tipo: diff > 0 ? 'entrada' : 'salida',
            p_cantidad: Math.abs(diff),
            p_costo_unitario: diff > 0 ? (Number(costoAjuste) || Number(form.precio_compra) || 0) : null,
            p_motivo: 'Ajuste manual de stock',
            p_usuario_id: user?.id,
            p_operacion_id: `ajuste:${editingId}:${Date.now()}`,
            p_referencia: 'Ajuste de inventario',
            p_fecha: new Date().toISOString(),
          });
          if (movError) throw movError;

          await supabase.from('auditoria').insert({
            usuario_id: user?.id,
            accion: 'UPDATE_STOCK',
            modulo: 'productos',
            detalle: JSON.stringify({
              producto_id: editingId,
              nombre: oldProduct?.nombre || '',
              stock_anterior: oldStock,
              stock_nuevo: newStock,
            }),
          });
        }

        showToast('Producto actualizado exitosamente', 'success');
      } else {
        const stockInicial = Number(form.stock) || 0;
        const { data: newProd, error } = await supabase
          .from('productos')
          .insert({ ...productoData, stock: 0 })
          .select('id, nombre')
          .single();
        if (error) throw error;

        if (newProd) {
          await supabase.from('auditoria').insert({
            usuario_id: user?.id,
            accion: 'CREATE',
            modulo: 'productos',
            detalle: JSON.stringify({ producto_id: newProd.id, nombre: newProd.nombre, stock_inicial: stockInicial }),
          });

          if (stockInicial > 0) {
            const { error: movError } = await supabase.rpc('cpp_registrar_movimiento', {
              p_producto_id: newProd.id,
              p_tipo: 'entrada',
              p_cantidad: stockInicial,
              p_costo_unitario: Number(form.precio_compra) || 0,
              p_motivo: 'Inventario Inicial',
              p_usuario_id: user?.id,
              p_operacion_id: `init:${newProd.id}`,
              p_referencia: 'Inventario inicial',
              p_fecha: new Date().toISOString(),
            });
            if (movError) throw movError;
          }
        }

        showToast('Producto creado exitosamente', 'success');
      }

      setModalOpen(false);
      fetchData();
    } catch (err: any) {
      showToast(err?.message || 'Error al guardar el producto', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
  };

  const executeDelete = async () => {
    if (!deleteId) return;
    try {
      const { error } = await supabase.from('productos').delete().eq('id', deleteId);
      if (error) throw error;
      showToast('Producto eliminado', 'success');
      fetchData();
    } catch {
      showToast('Error al eliminar el producto', 'error');
    } finally {
      setDeleteId(null);
    }
  };

  const getStatusBadge = (stock: number, stockMinimo: number) => {
    if (stock === 0) {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#DC2626]/10 text-[#DC2626] whitespace-nowrap">Agotado</span>;
    }
    if (stock <= stockMinimo) {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#F59E0B]/10 text-[#F59E0B] whitespace-nowrap">Bajo</span>;
    }
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#16A34A]/10 text-[#16A34A] whitespace-nowrap">Disponible</span>;
  };

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const stockActualEdicion = editingId
    ? (productos.find((p) => p.id === editingId)?.stock ?? 0)
    : 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="spinner w-10 h-10 border-3 border-[#2563EB] border-t-transparent mx-auto mb-4"></div>
            <p className="text-sm text-gray-500">Cargando productos...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in space-y-6">
        {toast && (
          <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium animate-slide-in ${
            toast.type === 'success' ? 'bg-[#16A34A] text-white' : 'bg-[#DC2626] text-white'
          }`}>
            {toast.message}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-[#111827]">Productos</h2>
            <p className="text-sm text-gray-500 mt-0.5">Gestión de inventario ({productos.length} productos)</p>
          </div>
          <button
            onClick={openNewModal}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2563EB] text-white rounded-lg text-sm font-medium hover:bg-[#1E40AF] transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line w-4 h-4 flex items-center justify-center"></i>
            Nuevo Producto
          </button>
        </div>

        <div className="bg-white rounded-xl p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 flex items-center justify-center"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                placeholder="Buscar por nombre, marca, código..."
                className="w-full pl-10 pr-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
              />
            </div>
            <select
              value={tipoFilter}
              onChange={(e) => { setTipoFilter(e.target.value); setCurrentPage(1); }}
              className="px-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-[#2563EB] cursor-pointer"
            >
              <option value="">Todos los tipos de producto</option>
              {tiposProducto.map((tipo) => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-white rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Producto</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Marca</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Tipo de producto</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Medida</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">P. Compra</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">P. Venta</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Stock</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Estado</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((prod) => (
                  <tr key={prod.id} className="border-b border-gray-50 hover:bg-[#F4F6F8]/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-[#E8ECEF] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {prod.imagen_url ? (
                            <img src={prod.imagen_url} alt={prod.nombre} className="w-9 h-9 object-cover" />
                          ) : (
                            <i className="ri-box-3-line text-gray-500 w-4 h-4 flex items-center justify-center"></i>
                          )}
                        </div>
                        <span className="text-sm font-medium text-[#111827] truncate max-w-[180px]">{prod.nombre}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{prod.marca || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {prod.tipo_producto || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono whitespace-nowrap">{prod.codigo_interno || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right whitespace-nowrap">Bs. {Number(prod.precio_compra).toLocaleString('es-BO')}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-[#111827] text-right whitespace-nowrap">Bs. {Number(prod.precio_venta).toLocaleString('es-BO')}</td>
                    <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                      <span className={prod.stock === 0 ? 'text-[#DC2626] font-semibold' : prod.stock <= prod.stock_minimo ? 'text-[#F59E0B] font-semibold' : 'text-[#111827]'}>
                        {prod.stock}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">{getStatusBadge(prod.stock, prod.stock_minimo)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEditModal(prod)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F4F6F8] text-gray-400 hover:text-[#2563EB] transition-colors cursor-pointer"
                          title="Editar"
                        >
                          <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(prod.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F4F6F8] text-gray-400 hover:text-[#DC2626] transition-colors cursor-pointer"
                          title="Eliminar"
                        >
                          <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {paginated.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-400">
                      {productos.length === 0 ? 'No hay productos registrados. Crea tu primer producto para comenzar.' : 'No se encontraron productos con los filtros actuales.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filtered.length)} de {filtered.length}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F4F6F8] text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm cursor-pointer ${
                      currentPage === page
                        ? 'bg-[#2563EB] text-white'
                        : 'hover:bg-[#F4F6F8] text-gray-600'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F4F6F8] text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                </button>
              </div>
            </div>
          )}
        </div>

        <ConfirmDialog
          open={!!deleteId}
          onClose={() => setDeleteId(null)}
          onConfirm={executeDelete}
          title="¿Eliminar producto?"
          message="El producto será eliminado permanentemente del sistema."
          confirmText="Sí, eliminar"
          confirmColor="danger"
          icon="ri-delete-bin-line"
        />

        <div className="bg-white rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#F59E0B]/10 rounded-lg flex items-center justify-center">
                <i className="ri-history-line text-[#F59E0B] w-4 h-4 flex items-center justify-center"></i>
              </div>
              <h3 className="text-base font-semibold text-[#111827]">Historial de Productos</h3>
            </div>
            <div className="flex items-center gap-1 bg-[#F4F6F8] rounded-lg p-1">
              {(['dia', 'semana', 'mes'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setHistoryPeriod(period)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    historyPeriod === period
                      ? 'bg-white text-[#111827] shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {period === 'dia' ? 'Hoy' : period === 'semana' ? 'Esta Semana' : 'Este Mes'}
                </button>
              ))}
            </div>
          </div>

          <div className="p-5">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-10">
                <div className="spinner w-7 h-7 border-2 border-[#2563EB] border-t-transparent"></div>
              </div>
            ) : historyData.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 bg-[#F4F6F8] rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-inbox-line text-gray-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <p className="text-sm text-gray-400">Sin actividad en este período</p>
                <p className="text-xs text-gray-300 mt-1">Los movimientos de productos aparecerán aquí</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  const nuevos = historyData.filter((h) => h.accion === 'CREATE');
                  const edits = historyData.filter((h) => h.accion === 'UPDATE_STOCK');
                  return (
                    <>
                      {nuevos.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#16A34A] mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#16A34A]"></span>
                            Productos Nuevos ({nuevos.length})
                          </h4>
                          <div className="space-y-2">
                            {nuevos.map((entry) => {
                              let detail: any = {};
                              try { detail = JSON.parse(entry.detalle); } catch (_e) { detail = {}; }
                              const date = new Date(entry.created_at);
                              const timeStr = date.toLocaleDateString('es-BO', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + date.toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' });
                              return (
                                <div key={entry.id} className="flex items-center gap-3 px-4 py-2.5 bg-[#F0FDF4] rounded-lg">
                                  <div className="w-9 h-9 bg-[#16A34A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <i className="ri-add-circle-line text-[#16A34A] w-4 h-4 flex items-center justify-center"></i>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-[#111827] truncate">{detail.nombre || 'Producto'}</p>
                                    <p className="text-xs text-gray-500">Stock inicial: <strong>{detail.stock_inicial || 0}</strong> unidades</p>
                                  </div>
                                  <span className="text-xs text-gray-400 whitespace-nowrap">{timeStr}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {edits.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#F59E0B] mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#F59E0B]"></span>
                            Stock Editado ({edits.length})
                          </h4>
                          <div className="space-y-2">
                            {edits.map((entry) => {
                              let detail: any = {};
                              try { detail = JSON.parse(entry.detalle); } catch (_e) { detail = {}; }
                              const date = new Date(entry.created_at);
                              const timeStr = date.toLocaleDateString('es-BO', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + date.toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' });
                              const diff = (detail.stock_nuevo || 0) - (detail.stock_anterior || 0);
                              return (
                                <div key={entry.id} className="flex items-center gap-3 px-4 py-2.5 bg-[#FFFBEB] rounded-lg">
                                  <div className="w-9 h-9 bg-[#F59E0B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <i className="ri-edit-line text-[#F59E0B] w-4 h-4 flex items-center justify-center"></i>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-[#111827] truncate">{detail.nombre || 'Producto'}</p>
                                    <p className="text-xs text-gray-500">
                                      Stock: <strong className="text-[#DC2626]">{detail.stock_anterior || 0}</strong>
                                      {' → '}
                                      <strong className="text-[#16A34A]">{detail.stock_nuevo || 0}</strong>
                                      {' '}
                                      <span className={diff > 0 ? 'text-[#16A34A]' : diff < 0 ? 'text-[#DC2626]' : 'text-gray-400'}>
                                        ({diff > 0 ? '+' : ''}{diff})
                                      </span>
                                    </p>
                                  </div>
                                  <span className="text-xs text-gray-400 whitespace-nowrap">{timeStr}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {modalOpen && (
          <>
            <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !saving && setModalOpen(false)}></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
                <div className="p-6 space-y-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-[#111827]">
                      {editingId ? 'Editar Producto' : 'Nuevo Producto'}
                    </h3>
                    <button
                      onClick={() => !saving && setModalOpen(false)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F4F6F8] cursor-pointer"
                    >
                      <i className="ri-close-line text-gray-500 w-5 h-5 flex items-center justify-center"></i>
                    </button>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-5">
                    <div className="flex flex-col items-center gap-2 flex-shrink-0">
                      <div
                        className="w-32 h-32 bg-[#F4F6F8] rounded-xl flex items-center justify-center overflow-hidden cursor-pointer border-2 border-dashed border-gray-200 hover:border-[#2563EB] transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {imagePreview ? (
                          <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                          <div className="text-center">
                            <i className="ri-image-add-line text-gray-400 text-2xl w-8 h-8 flex items-center justify-center mx-auto"></i>
                            <p className="text-xs text-gray-400 mt-1">Subir imagen</p>
                          </div>
                        )}
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      {imagePreview && (
                        <button
                          onClick={() => { setImageFile(null); setImagePreview(null); }}
                          className="text-xs text-[#DC2626] hover:underline cursor-pointer"
                        >
                          Quitar imagen
                        </button>
                      )}
                    </div>

                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                        <input
                          type="text"
                          value={form.nombre}
                          onChange={(e) => updateField('nombre', e.target.value)}
                          className="w-full px-3 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                          placeholder="Nombre del producto"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de producto</label>
                        <input
                          type="text"
                          value={form.tipo_producto}
                          onChange={(e) => updateField('tipo_producto', e.target.value)}
                          className="w-full px-3 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                          placeholder="Neumático, Lubricante, Filtro, Repuesto de motor..."
                          list="tipos-producto-sugeridos"
                        />
                        <datalist id="tipos-producto-sugeridos">
                          <option value="Neumático" />
                          <option value="Lubricante" />
                          <option value="Aceite" />
                          <option value="Filtro" />
                          <option value="Repuesto de motor" />
                          <option value="Accesorio" />
                          <option value="Batería" />
                          <option value="Freno" />
                        </datalist>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                        <input
                          type="text"
                          value={form.marca}
                          onChange={(e) => updateField('marca', e.target.value)}
                          className="w-full px-3 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                          placeholder="Marca"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Medida</label>
                        <input
                          type="text"
                          value={form.codigo_interno}
                          onChange={(e) => updateField('codigo_interno', e.target.value)}
                          className="w-full px-3 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                          placeholder="Medida del producto"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Modelo Compatible</label>
                        <input
                          type="text"
                          value={form.modelo_compatible}
                          onChange={(e) => updateField('modelo_compatible', e.target.value)}
                          className="w-full px-3 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                          placeholder="Modelo compatible"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Precio Compra</label>
                        <input
                          type="number"
                          value={form.precio_compra}
                          onChange={(e) => updateField('precio_compra', e.target.value)}
                          className="w-full px-3 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Precio Venta *</label>
                        <input
                          type="number"
                          value={form.precio_venta}
                          onChange={(e) => updateField('precio_venta', e.target.value)}
                          className="w-full px-3 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Stock Inicial</label>
                        <input
                          type="number"
                          value={form.stock}
                          onChange={(e) => updateField('stock', e.target.value)}
                          className="w-full px-3 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Stock Mínimo</label>
                        <input
                          type="number"
                          value={form.stock_minimo}
                          onChange={(e) => updateField('stock_minimo', e.target.value)}
                          className="w-full px-3 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      {editingId && Number(form.stock) > stockActualEdicion && (
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Costo unitario del ajuste (aumento de stock)</label>
                          <input
                            type="number"
                            value={costoAjuste}
                            onChange={(e) => setCostoAjuste(e.target.value)}
                            className="w-full px-3 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                            placeholder="Costo por unidad"
                            min="0"
                          />
                          <p className="text-xs text-gray-400 mt-1">Se usará para recalcular el CPP. Si se deja vacío, se toma el precio de compra.</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor</label>
                        <input
                          type="text"
                          value={form.proveedor}
                          onChange={(e) => updateField('proveedor', e.target.value)}
                          className="w-full px-3 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                          placeholder="Proveedor"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
                        <input
                          type="text"
                          value={form.ubicacion}
                          onChange={(e) => updateField('ubicacion', e.target.value)}
                          className="w-full px-3 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                          placeholder="Estante / pasillo"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => setModalOpen(false)}
                      disabled={saving}
                      className="flex-1 py-2.5 bg-[#F4F6F8] text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex-1 py-2.5 bg-[#2563EB] text-white rounded-lg text-sm font-medium hover:bg-[#1E40AF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                    >
                      {saving ? (
                        <>
                          <div className="spinner w-4 h-4 border-2 border-white border-t-transparent"></div>
                          <span>Guardando...</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                          <span>{editingId ? 'Actualizar Producto' : 'Crear Producto'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}